import { Component, inject, OnInit, OnDestroy, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ArtistStatementService, ArtistStatement, ArtistStatementItem } from '../services/artist-statement.service';
import { CompanyService } from '../services/company.service';
import { ArtistService } from '../services/artist.service';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { ArtistStatementTemplateComponent } from './artist-statement-template/artist-statement-template.component';
import { ArtistMonthlyStatementsComponent } from './monthly-statements/artist-monthly-statements.component';
import { ActivatedRoute, Router } from '@angular/router';

export type ArtistStatementView = 'monthly' | 'manual';

@Component({
  selector: 'app-artist-statement',
  standalone: true,
  imports: [CommonModule, FormsModule, ArtistStatementTemplateComponent, ArtistMonthlyStatementsComponent],
  templateUrl: './artist-statement.component.html',
  styleUrls: ['./artist-statement.component.scss']
})
export class ArtistStatementComponent implements OnInit, OnDestroy {
  private statementService = inject(ArtistStatementService);
  private companyService = inject(CompanyService);
  private artistService = inject(ArtistService);
  private pdfService = inject(PdfGeneratorService);
  private cd = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  /** KAN-147: Monthly statements is the default tab; `?view=manual` opens the manual list. */
  view: ArtistStatementView = this.route.snapshot.queryParamMap.get('view') === 'manual' ? 'manual' : 'monthly';

  statements: ArtistStatement[] = [];
  companies: any[] = [];
  artists: any[] = [];

  // Data Subscriptions
  private subs: Subscription = new Subscription();

  // Filter/Selection States
  selectedPeriod: string = '';
  periods: string[] = [];
  
  isLoadingGrid = true;
  isModalOpen = false;
  isSaving = false;
  isUploadingImage = false;
  isGeneratingPdf = false;
  isViewModalOpen = false;
  selectedStatementForPdf: ArtistStatement | null = null;

  // New Statement Modal State
  newStatement: Partial<ArtistStatement> = {
    period: '',
    companyId: '',
    artistId: '',
    items: [],
    totalQuantity: 0,
    totalAmount: 0
  };

  newItem: Partial<ArtistStatementItem> = {
    frameId: '',
    frameImage: '',
    quantity: 1,
    commission: 0,
    total: 0
  };

  constructor() {
    this.generatePeriods();
  }

  ngOnInit() {
    this.subs.add(
      combineLatest([
        this.statementService.getStatements(),
        this.companyService.getCompanies(),
        this.artistService.getArtists()
      ]).subscribe(([statements, companies, artists]) => {
        // KAN-147: automated monthly snapshots live in the Monthly tab; this list stays manual-only
        this.statements = statements.filter(s => !s.isDeleted && s.source !== 'automated');
        this.companies = companies.filter((c: any) => !c.isDeleted);
        this.artists = artists.filter(a => !a.isDeleted && a.isActive);
        this.isLoadingGrid = false;
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  setView(view: ArtistStatementView, focus = false) {
    this.view = view;
    this.router.navigate([], { relativeTo: this.route, queryParams: { view }, queryParamsHandling: 'merge', replaceUrl: true });
    if (focus) setTimeout(() => document.getElementById(`tab-${view}`)?.focus());
  }

  generatePeriods() {
    const startDate = new Date(2024, 0, 1);
    const currentDate = new Date();
    const tempDate = new Date(startDate);

    while (tempDate <= currentDate) {
      const year = tempDate.getFullYear();
      const month = (tempDate.getMonth() + 1).toString().padStart(2, '0');
      this.periods.push(`${year}/${month}`);
      tempDate.setMonth(tempDate.getMonth() + 1);
    }
    this.periods.reverse();
    if (this.periods.length > 0) {
      this.selectedPeriod = this.periods[0];
    }
  }

  get filteredStatements() {
    if (!this.selectedPeriod) return this.statements;
    if (this.selectedPeriod === 'All Periods') return this.statements;
    return this.statements.filter(s => s.period === this.selectedPeriod);
  }

  openCreateModal() {
    this.newStatement = {
      period: this.selectedPeriod !== 'All Periods' ? this.selectedPeriod : this.periods[0],
      companyId: '',
      artistId: '',
      items: [],
      totalQuantity: 0,
      totalAmount: 0
    };
    this.resetNewItem();
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onArtistChange() {
    const artist = this.artists.find(a => a.id === this.newStatement.artistId);
    if (artist) {
      this.newItem.commission = artist.commission_amount || 0;
      this.calculateItemTotal();
      
      // Update commission on existing items if artist changes
      if (this.newStatement.items && this.newStatement.items.length > 0) {
        this.newStatement.items.forEach(item => {
          item.commission = artist.commission_amount || 0;
          item.total = item.quantity * item.commission;
        });
        this.calculateStatementTotals();
      }
    }
  }

  calculateItemTotal() {
    this.newItem.total = (this.newItem.quantity || 0) * (this.newItem.commission || 0);
  }

  addItem() {
    if (!this.newItem.frameId || !this.newItem.quantity) return;
    
    this.newStatement.items = this.newStatement.items || [];
    this.newStatement.items.push({
      frameId: this.newItem.frameId,
      frameImage: this.newItem.frameImage || '',
      quantity: this.newItem.quantity,
      commission: this.newItem.commission || 0,
      total: this.newItem.total || 0
    });
    
    this.calculateStatementTotals();
    this.resetNewItem();
  }

  async removeItem(index: number) {
    if (!this.newStatement.items) return;
    
    const removedItem = this.newStatement.items[index];
    if (removedItem.frameImage) {
      try {
        await this.artistService.deleteFile(removedItem.frameImage);
      } catch (error) {
        console.error('Failed to delete file from storage', error);
      }
    }
    
    this.newStatement.items.splice(index, 1);
    this.calculateStatementTotals();
  }

  resetNewItem() {
    const artist = this.artists.find(a => a.id === this.newStatement.artistId);
    this.newItem = {
      frameId: '',
      frameImage: '',
      quantity: 1,
      commission: artist ? (artist.commission_amount || 0) : 0,
      total: 0
    };
    this.calculateItemTotal();
  }

  calculateStatementTotals() {
    if (!this.newStatement.items) return;
    this.newStatement.totalQuantity = this.newStatement.items.reduce((sum, item) => sum + item.quantity, 0);
    this.newStatement.totalAmount = this.newStatement.items.reduce((sum, item) => sum + item.total, 0);
  }

  async saveStatement() {
    if (!this.newStatement.companyId || !this.newStatement.artistId || !this.newStatement.period) {
      alert('Please fill in all required fields (Period, Company, Artist).');
      return;
    }

    if (!this.newStatement.items || this.newStatement.items.length === 0) {
      alert('Please add at least one item to the statement.');
      return;
    }

    this.isSaving = true;
    try {
      const company = this.companies.find(c => c.id === this.newStatement.companyId);
      const artist = this.artists.find(a => a.id === this.newStatement.artistId);
      
      this.newStatement.companyName = company ? company.company : '';
      this.newStatement.artistName = artist ? artist.name : '';

      await this.statementService.addStatement(this.newStatement as ArtistStatement);
      this.closeModal();
    } catch (error) {
      console.error('Error saving statement:', error);
      alert('Failed to save statement.');
    } finally {
      this.isSaving = false;
    }
  }

  async deleteStatement(id: string) {
    if (confirm('Are you sure you want to delete this statement?')) {
      await this.statementService.deleteStatement(id);
    }
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0];
    if (!file) return;

    if (!this.newStatement.artistId || !this.newStatement.period) {
      alert('Please select a Statement Period and an Artist before uploading an image.');
      event.target.value = ''; // reset
      return;
    }

    this.isUploadingImage = true;
    try {
      const convertedFile = await this.convertToJpg(file);
      const result = await this.artistService.uploadFrameImage(this.newStatement.artistId, this.newStatement.period as string, convertedFile);
      this.newItem.frameImage = result.url;
    } catch (error) {
      console.error('Upload failed', error);
      alert('Failed to upload image');
    } finally {
      this.isUploadingImage = false;
      event.target.value = '';
    }
  }

  convertToJpg(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject('No context');
          
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);

          canvas.toBlob((blob) => {
            if (!blob) return reject('Canvas empty');
            const newFile = new File([blob], file.name.split('.')[0] + '.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(newFile);
          }, 'image/jpeg', 0.8);
        };
        img.onerror = () => reject('Image load error');
        img.src = e.target.result;
      };
      reader.onerror = () => reject('File read error');
      reader.readAsDataURL(file);
    });
  }

  async removeImage() {
    if (this.newItem.frameImage) {
      try {
        await this.artistService.deleteFile(this.newItem.frameImage);
      } catch (error) {
        console.error('Failed to delete file from storage', error);
      }
    }
    this.newItem.frameImage = '';
  }

  triggerFileInput(fileInput: HTMLInputElement) {
    if (fileInput) {
      fileInput.click();
    }
  }

  viewStatement(statement: ArtistStatement) {
    this.selectedStatementForPdf = statement;
    this.isViewModalOpen = true;
  }

  closeViewModal() {
    this.isViewModalOpen = false;
    this.selectedStatementForPdf = null;
  }

  async openPdfInNewTab() {
    if (!this.selectedStatementForPdf) return;
    this.isGeneratingPdf = true;
    
    let pdfWindow: Window | null = null;
    pdfWindow = window.open('', '_blank');
    if (pdfWindow) {
      pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
    }

    try {
      this.cd.detectChanges();

      await new Promise(resolve => setTimeout(resolve, 800));

      const content = document.getElementById('pdf-content');
      if (content) {
        const statement = this.selectedStatementForPdf;
        const filename = `Artist_Statement_${statement.period.replace('/', '-')}_${statement.artistName.replace(' ', '_')}.pdf`;
        await this.pdfService.openPdf(content, filename, 'portrait', pdfWindow || undefined);
      } else {
        if (pdfWindow) pdfWindow.close();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      if (pdfWindow) pdfWindow.close();
    } finally {
      this.isGeneratingPdf = false;
    }
  }

  async savePdf() {
    if (!this.selectedStatementForPdf) return;
    this.isGeneratingPdf = true;
    
    try {
      this.cd.detectChanges();
      await new Promise(resolve => setTimeout(resolve, 800));

      const content = document.getElementById('pdf-content');
      if (content) {
        const statement = this.selectedStatementForPdf;
        const filename = `Artist_Statement_${statement.period.replace('/', '-')}_${statement.artistName.replace(' ', '_')}.pdf`;
        await this.pdfService.savePdf(content, filename, 'portrait');
      }
    } catch (error) {
      console.error('Error saving PDF:', error);
    } finally {
      this.isGeneratingPdf = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscKey(event: Event) {
    if (this.isViewModalOpen) {
      this.closeViewModal();
    } else if (this.isModalOpen) {
      this.closeModal();
    }
  }
}
